import { POINT_LOCATION } from '@/interfaces/map.interface'
import { Drawing, Orthophoto, PointCoordinates } from '@/interfaces/misc.interface'
import { OutdoorPlan, OrthoPhoto } from '@/interfaces/realEstate.interface'
import { VisualLayerFloorPlan } from '@/services/floorPlan.service'
import type { Entity } from '@/services/entity.service'
import i18n from '@/i18n'

function coordinatesToPointCoordinates(coordinates: POINT_LOCATION): PointCoordinates {
  // only use first 2 coordinates, because that's how mapbox wants them
  return [coordinates.longitude, coordinates.latitude]
}
function getTitle(type: string, title?: string): string {
  const { t } = i18n.global

  if (title) {
    return title
  }
  const typeLowerCase = type.split('_')[0].toLowerCase()
  return t(`general__text__drawing-${typeLowerCase}`)
}

export function convertOutdoorPlanToDrawing(outdoorPlan: OutdoorPlan): Drawing {
  return {
    id: outdoorPlan.id,
    type: outdoorPlan.type,
    title: getTitle(outdoorPlan.type, outdoorPlan.title),
    northWest: coordinatesToPointCoordinates(outdoorPlan.northWest),
    northEast: coordinatesToPointCoordinates(outdoorPlan.northEast),
    southEast: coordinatesToPointCoordinates(outdoorPlan.southEast),
    southWest: coordinatesToPointCoordinates(outdoorPlan.southWest),
    storageFileName: outdoorPlan.storageFileName,
    tiledLayerUri: outdoorPlan.tiledLayerUri,
    visible: false
  }
}

export function convertFloorPlanToDrawing(floorPlan: any): Drawing {
  return {
    id: floorPlan.id,
    type: floorPlan.type,
    title: getTitle(floorPlan.type, floorPlan.title),
    northWest: floorPlan.northWest,
    northEast: floorPlan.northEast,
    southEast: floorPlan.southEast,
    southWest: floorPlan.southWest,
    storageFileName: floorPlan.storageFileName,
    tiledLayerUri: floorPlan.tiledLayerUri,
    visible: false
  }
}

export function convertVisualLayerToDrawing(floorPlan: VisualLayerFloorPlan): Drawing {
  return {
    id: floorPlan.id,
    type: floorPlan.type,
    title: floorPlan.name,
    northWest: floorPlan.nw.coordinates,
    northEast: floorPlan.ne.coordinates,
    southEast: floorPlan.se.coordinates,
    southWest: floorPlan.sw.coordinates,
    fileId: floorPlan.fileId,
    visible: false
  }
}

export function convertEntityToDrawing(entity: Entity): Drawing | null {
  const components = entity.components as any[]

  const getLabelValue = (name: string): string | undefined =>
    components.find((c) => c.type === 'label' && c.name === name)?.value

  const getPointCoords = (name: string): PointCoordinates | null => {
    const pt = components.find((c) => c.type === 'point' && c.name === name)
    return pt?.geometry?.coordinates ?? null
  }

  const nw = getPointCoords('nw')
  const ne = getPointCoords('ne')
  const se = getPointCoords('se')
  const sw = getPointCoords('sw')

  if (!nw || !ne || !se || !sw) return null

  const georefRelation = components.find(
    (c) => c.type === 'relation' && c.relationType === 'GEOREFERENCES'
  )

  return {
    id: entity.id,
    title: getLabelValue('name') ?? entity.name,
    type: getLabelValue('floorPlanType') ?? 'OTHER',
    northWest: nw,
    northEast: ne,
    southEast: se,
    southWest: sw,
    fileId: georefRelation?.targetId,
    visible: false
  }
}

export function convertOutdoorImageToOrthophoto(image: OrthoPhoto, index: number): Orthophoto {
  const { t } = i18n.global
  return {
    id: image.id,
    // type: image.type, // Currently doesn't exist and should not be necessary?
    title: image.title
      ? image.title
      : index !== 0
        ? t('general__view__orthophoto') + ' ' + index + 1
        : t('general__view__orthophoto'),
    northWest: coordinatesToPointCoordinates(image.northWest),
    northEast: coordinatesToPointCoordinates(image.northEast),
    southEast: coordinatesToPointCoordinates(image.southEast),
    southWest: coordinatesToPointCoordinates(image.southWest),
    storageFileName: image.storageFileName,
    tiledLayerUri: image.tiledLayerUri,
    visible: false
  }
}
